import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Camera as CameraIcon, RotateCcw, Check, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface UnifiedCameraCaptureProps {
  onCapture: (file: File) => void;
  onClose: () => void;
  className?: string;
}

export function UnifiedCameraCapture({
  onCapture,
  onClose,
  className,
}: UnifiedCameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  // Atualizar stream no vídeo
  useEffect(() => {
    const video = videoRef.current;
    if (!stream || !video) {
      setIsVideoReady(false);
      return;
    }

    setIsVideoReady(false);
    video.srcObject = stream;
  }, [stream]);

  // Aguardar vídeo estar pronto
  useEffect(() => {
    // Limpar cleanup anterior
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const video = videoRef.current;
    if (!video || !stream) {
      return;
    }

    // Aguardar um pouco para garantir que o srcObject foi definido
    const checkAndSetup = () => {
      const currentVideo = videoRef.current;
      if (!currentVideo || !currentVideo.srcObject || currentVideo.srcObject !== stream) {
        // Aguardar mais um pouco
        timeoutRef.current = setTimeout(checkAndSetup, 50);
        return;
      }

      let isReady = false;

      const markAsReady = () => {
        if (!isReady) {
          isReady = true;
          setIsVideoReady(true);
          setIsLoading(false);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }
      };

      const handleLoadedMetadata = () => {
        // Tentar fazer play do vídeo
        currentVideo.play().catch((err) => {
          console.error("Erro ao fazer play do vídeo:", err);
        });
      };

      const handlePlaying = () => {
        markAsReady();
      };

      const handleLoadedData = () => {
        markAsReady();
      };

      const handleCanPlay = () => {
        markAsReady();
      };

      const handleError = (e: Event) => {
        console.error("Erro no vídeo:", e);
        setIsVideoReady(false);
        setIsLoading(false);
        setError("Erro ao carregar vídeo da câmera");
      };

      // Adicionar listeners
      currentVideo.addEventListener("loadedmetadata", handleLoadedMetadata);
      currentVideo.addEventListener("loadeddata", handleLoadedData);
      currentVideo.addEventListener("playing", handlePlaying);
      currentVideo.addEventListener("canplay", handleCanPlay);
      currentVideo.addEventListener("error", handleError);

      // Tentar fazer play imediatamente se já tiver metadados
      if (currentVideo.readyState >= 2) {
        currentVideo.play().catch((err) => {
          console.error("Erro ao fazer play do vídeo:", err);
        });
      }

      // Fallback: se após 3 segundos o vídeo não estiver pronto, considerar como pronto
      timeoutRef.current = setTimeout(() => {
        if (currentVideo.srcObject && currentVideo.readyState >= 1 && !isReady) {
          console.warn("Timeout aguardando vídeo, forçando como pronto");
          markAsReady();
        }
      }, 3000);

      // Armazenar função de cleanup
      cleanupRef.current = () => {
        currentVideo.removeEventListener("loadedmetadata", handleLoadedMetadata);
        currentVideo.removeEventListener("loadeddata", handleLoadedData);
        currentVideo.removeEventListener("playing", handlePlaying);
        currentVideo.removeEventListener("canplay", handleCanPlay);
        currentVideo.removeEventListener("error", handleError);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    };

    checkAndSetup();

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [stream]);

  const startCamera = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsVideoReady(false);
      setError(null);

      // Parar stream anterior se existir usando functional update
      setStream((prevStream) => {
        if (prevStream) {
          prevStream.getTracks().forEach((track) => track.stop());
        }
        return null;
      });

      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      // Não definir isLoading como false aqui - aguardar o vídeo estar pronto
    } catch (err) {
      console.error("Erro ao acessar câmera:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Não foi possível acessar a câmera. Verifique as permissões.";
      setError(errorMessage);
      setIsLoading(false);
      setIsVideoReady(false);
      toast.error("Erro ao acessar câmera");
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setIsVideoReady(false);
  }, [stream]);

  const switchCamera = useCallback(() => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  }, []);

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !stream) return;

    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      // Obter dimensões reais do vídeo
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      if (videoWidth === 0 || videoHeight === 0) {
        throw new Error("Dimensões de vídeo inválidas");
      }

      // Aspect ratio padrão: 3/4 (portrait)
      const targetAspect = 3 / 4;
      const canvasWidth = 1080;
      const canvasHeight = 1440; // 1080 * 4/3 = 1440

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Não foi possível obter contexto do canvas");
      }

      // Calcular aspect ratio do vídeo
      const videoAspect = videoWidth / videoHeight;

      // Calcular área de corte para manter aspect ratio 3/4
      let sourceX = 0;
      let sourceY = 0;
      let sourceWidth = videoWidth;
      let sourceHeight = videoHeight;

      if (videoAspect > targetAspect) {
        // Vídeo é mais largo, cortar largura
        sourceHeight = videoHeight;
        sourceWidth = videoHeight * targetAspect;
        sourceX = (videoWidth - sourceWidth) / 2;
      } else {
        // Vídeo é mais alto, cortar altura
        sourceWidth = videoWidth;
        sourceHeight = videoWidth / targetAspect;
        sourceY = (videoHeight - sourceHeight) / 2;
      }

      // Limpar canvas
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Aplicar espelhamento para câmera frontal
      if (facingMode === "user") {
        ctx.save();
        ctx.scale(-1, 1);
        ctx.drawImage(
          video,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          -canvasWidth,
          0,
          canvasWidth,
          canvasHeight
        );
        ctx.restore();
      } else {
        // Câmera traseira - desenhar normalmente
        ctx.drawImage(
          video,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          canvasWidth,
          canvasHeight
        );
      }

      // Criar preview e blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
            setPreviewImage(dataUrl);
            setCapturedBlob(blob);
          }
          setIsCapturing(false);
        },
        "image/jpeg",
        0.95
      );
    } catch (err) {
      console.error("Erro ao capturar foto:", err);
      toast.error("Erro ao capturar foto");
      setIsCapturing(false);
    }
  }, [stream, facingMode]);

  const retakePhoto = useCallback(() => {
    setPreviewImage(null);
    setCapturedBlob(null);
  }, []);

  const confirmPhoto = useCallback(async () => {
    if (!capturedBlob) return;

    try {
      const file = new File(
        [capturedBlob],
        `camera_capture_${Date.now()}.jpg`,
        { type: "image/jpeg" }
      );

      onCapture(file);
      stopCamera();
    } catch (err) {
      console.error("Erro ao processar foto:", err);
      toast.error("Erro ao processar foto");
    }
  }, [capturedBlob, onCapture, stopCamera]);

  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [stopCamera, onClose]);

  // Inicializar câmera
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  // Prevenir scroll do body quando o componente está montado
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 bg-background flex flex-col",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3 bg-background/95 backdrop-blur-sm">
        <h2 className="text-lg font-semibold">Capturar Foto</h2>
        <Button variant="ghost" size="icon" onClick={handleClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Conteúdo principal */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-auto">
        {error ? (
          // Estado de erro
          <div className="flex flex-col items-center justify-center max-w-md w-full bg-destructive/10 border border-destructive/20 rounded-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mb-4">
              <X className="h-8 w-8 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Erro ao acessar câmera</h3>
            <p className="text-sm text-muted-foreground mb-6">{error}</p>
            <Button onClick={handleClose} variant="outline">
              Fechar
            </Button>
          </div>
        ) : previewImage ? (
          // Modo preview - foto capturada
          <div className="flex flex-col gap-6 w-full max-w-md">
            <div
              className="relative w-full bg-black rounded-xl overflow-hidden shadow-2xl"
              style={{ aspectRatio: "3/4" }}
            >
              <img
                src={previewImage}
                alt="Preview"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={retakePhoto}
                variant="outline"
                size="lg"
                className="flex-1"
              >
                <RotateCcw className="h-5 w-5 mr-2" />
                Tirar Novamente
              </Button>
              <Button
                onClick={confirmPhoto}
                size="lg"
                className="flex-1"
              >
                <Check className="h-5 w-5 mr-2" />
                Usar Esta Foto
              </Button>
            </div>
          </div>
        ) : (
          // Modo câmera - captura ao vivo
          <div className="flex flex-col gap-6 w-full max-w-md">
            <div
              className="relative w-full bg-black rounded-xl overflow-hidden shadow-2xl"
              style={{ aspectRatio: "3/4" }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={cn(
                  "w-full h-full object-cover",
                  facingMode === "user" && "scale-x-[-1]",
                  (!isVideoReady || isLoading || !stream) && "hidden"
                )}
              />
              {isLoading || !isVideoReady || !stream ? (
                <div className="flex items-center justify-center h-full text-white absolute inset-0">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <p className="text-sm">Preparando câmera...</p>
                  </div>
                </div>
              ) : null}
            </div>

            <canvas ref={canvasRef} className="hidden" />

            {/* Controles */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleClose}
                variant="outline"
                size="lg"
                className="flex-1"
              >
                <X className="h-5 w-5 mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={switchCamera}
                variant="outline"
                size="lg"
                className="flex-1"
                disabled={isLoading}
              >
                <RotateCcw className="h-5 w-5 mr-2" />
                Trocar Câmera
              </Button>
              <Button
                onClick={capturePhoto}
                size="lg"
                disabled={!stream || isCapturing || isLoading || !isVideoReady}
                className="flex-1"
              >
                {isCapturing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Capturando...
                  </>
                ) : (
                  <>
                    <CameraIcon className="h-5 w-5 mr-2" />
                    Capturar
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

